// ════════════════════════════════════════════════════════════════════
// TERROR — H6's FACE · THE REPLAY THEATRE
// ────────────────────────────────────────────────────────────────────
//   <ReplayShelf uid={uid} />                     the list
//   <ReplayTheatre uid={uid} id={id} onClose />   the projector
//
// The projector mounts the REAL Shatterfield with a `replayFeed` in
// place of its Firebase listener. There is no second renderer and there
// never will be: a replay that draws the board a second way is a replay
// that eventually disagrees with the game, and then nobody trusts
// either picture.
//
// ── READ-ONLY IS ONE LEVER, NOT AN AUDIT ─────────────────────────────
// setPlaybackLock(true) on mount, false on unmount, and in a finally so
// a thrown render cannot leave it on. It makes updateMatch,
// updatePlayer and endMatch no-ops, which is every board mutation in
// the game — so a tap during a replay does nothing regardless of what
// the Shatterfield puts on screen, and the next frame corrects any
// local optimism. startRecording() also clears the lock unconditionally,
// so even a catastrophic unmount cannot poison the next real duel.
//
// ── 1x / 2x, AND NOTHING ELSE ────────────────────────────────────────
// caP'n asked for two speeds. SPEEDS is [1, 2] in replay.js and this
// renders whatever is in it — so a third is a one-element edit if it is
// ever wanted, and until then there is no half-speed nobody asked for.
// ════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { GM, MANUSCRIPT_BG } from "./theme";
import Shatterfield from "./Shatterfield";
import {
  listReplays, loadReplay, deleteReplay, makeReplayFeed,
  setPlaybackLock, SPEEDS, KEEP_REPLAYS,
} from "./replay";

const fmtWhen = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    + " " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};
const fmtDur = (ms) => {
  const s = Math.max(0, Math.round((ms || 0) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

// ════════════════════════════════════════════════════════════════════
// THE SHELF
// ════════════════════════════════════════════════════════════════════
export function ReplayShelf({ uid }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);   // replay id being watched
  const [busy, setBusy] = useState(null);

  const refresh = React.useCallback(() => {
    if (!uid) { setRows([]); return; }
    listReplays(uid).then(setRows).catch(() => setRows([]));
  }, [uid]);

  useEffect(() => { refresh(); }, [refresh]);

  if (rows === null) {
    return <div style={{ padding: "24px 0", textAlign: "center", color: GM.vellumFaint, fontSize: 11, fontStyle: "italic" }}>◆ Winding the tape…</div>;
  }

  return (
    <div>
      <div style={{ fontSize: 11, color: GM.vellumFaint, fontStyle: "italic", lineHeight: 1.7, marginBottom: 12 }}>
        Every Solrend records itself. The last {KEEP_REPLAYS} are kept; older tapes
        make room for newer ones without being asked.
      </div>

      {!rows.length && (
        <div style={{ textAlign: "center", padding: "26px 8px", color: GM.vellumFaint, fontSize: 11, fontStyle: "italic", lineHeight: 1.7 }}>
          ◇ No tapes yet.<br />Finish a Solrend and the first one lands here.
        </div>
      )}

      <div style={{ display: "grid", gap: 8 }}>
        {rows.map((r) => {
          const won = r.won === true, lost = r.won === false;
          const accent = won ? GM.inkGoldBright : lost ? GM.inkRed : GM.vellumDim;
          return (
            <div key={r.id} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "9px 11px", borderRadius: 6,
              border: `1px solid ${GM.pageBorder}`, background: GM.pageMid,
            }}>
              <span style={{ color: accent, fontSize: 13, flex: "0 0 auto" }}>
                {won ? "◆" : lost ? "◇" : "·"}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: GM.vellum, fontFamily: GM.font, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {r.oppName || (r.mode === "npc" ? "The Vanguard" : "An Aenarchist")}
                  <span style={{ color: accent, marginLeft: 8, fontSize: 10 }}>
                    {won ? "WON" : lost ? "LOST" : "—"}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: GM.vellumFaint, marginTop: 2 }}>
                  {fmtWhen(r.at)} · {r.turns || 0} turns · {fmtDur(r.ms)} · {r.frames || 0} frames
                </div>
              </div>
              <button onClick={() => setOpen(r.id)} style={btn(GM.inkGoldBright)}>WATCH</button>
              <button
                onClick={async () => { setBusy(r.id); await deleteReplay(uid, r.id); setBusy(null); refresh(); }}
                style={btn(GM.vellumFaint)}>
                {busy === r.id ? "…" : "✕"}
              </button>
            </div>
          );
        })}
      </div>

      {open && <ReplayTheatre uid={uid} id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

const btn = (color) => ({
  all: "unset", cursor: "pointer", flex: "0 0 auto",
  fontFamily: "'Inter', system-ui, sans-serif", fontSize: 10, letterSpacing: "0.08em",
  padding: "5px 9px", borderRadius: 4,
  border: `1px solid ${color}55`, color,
});

// ════════════════════════════════════════════════════════════════════
// THE PROJECTOR
// ════════════════════════════════════════════════════════════════════
export function ReplayTheatre({ uid, id, onClose }) {
  const [replay, setReplay] = useState(null);
  const [err, setErr] = useState(null);
  const [prog, setProg] = useState({ idx: 0, total: 0, playing: false, speed: 1, ms: 0, totalMs: 0 });

  useEffect(() => {
    let dead = false;
    loadReplay(uid, id)
      .then((r) => { if (dead) return; r ? setReplay(r) : setErr("That tape is gone."); })
      .catch(() => !dead && setErr("The tape would not read."));
    return () => { dead = true; };
  }, [uid, id]);

  const feed = useMemo(() => (replay ? makeReplayFeed(replay, { speed: 1 }) : null), [replay]);

  // THE LOCK — on for exactly as long as this component is mounted.
  useEffect(() => {
    setPlaybackLock(true);
    return () => setPlaybackLock(false);
  }, []);

  useEffect(() => {
    if (!feed) return;
    const off = feed.onProgress(setProg);
    return () => { off(); feed.destroy(); };
  }, [feed]);

  // Which seat we are watching from. A replay of a duel you played is
  // watched from YOUR chair — anything else shows you a hand you never
  // held. Falls back to seat 0 for a tape whose seats no longer resolve.
  const seat = useMemo(() => {
    const players = replay?.seed?.players || {};
    for (const k of Object.keys(players)) if (players[k]?.uid === uid) return Number(k);
    return 0;
  }, [replay, uid]);

  // Both shells portal too — they are `position: fixed` for the same
  // reason the theatre is, and a loading screen pinned inside a 500px
  // modal is the same bug wearing a smaller coat.
  if (err) {
    return createPortal((
      <div style={shellStyle}>
        <div style={{ color: GM.vellumDim, fontStyle: "italic" }}>{err}</div>
        <button onClick={onClose} style={{ ...btn(GM.inkGoldBright), marginTop: 14 }}>← Back</button>
      </div>
    ), document.body);
  }
  if (!replay || !feed) {
    return createPortal((
      <div style={shellStyle}><div style={{ color: GM.vellumFaint, fontStyle: "italic" }}>◆ Winding…</div></div>
    ), document.body);
  }

  // PORTALLED TO <body>, and not for tidiness. The shelf is mounted
  // inside the Plaza's Bulletin Board, which lives inside the Plaza's
  // world — and any ancestor carrying a transform, filter or non-static
  // opacity turns `position: fixed` into "fixed relative to THAT", which
  // would pin a full-screen theatre inside a 500px modal. The Plaza pans
  // its camera with transforms. Three lines to make that class of bug
  // impossible instead of something to remember.
  //
  // ⚠ UNVERIFIED IN A BROWSER: this mounts the whole Shatterfield —
  // including its 3D field — while the Plaza's own scene is still
  // mounted behind it. That is two WebGL surfaces at once and it is the
  // first thing to look at in QA. If it bites, the fix is to route the
  // theatre through App at screen level rather than from the Plaza, and
  // the component is prop-only and portalled precisely so that move is
  // an import.
  return createPortal((
    <div style={{ position: "fixed", inset: 0, zIndex: 950, background: MANUSCRIPT_BG }}>
      {/* THE REAL RENDERER, fed from the tape. `code` is the original
          match code and is deliberately still passed — the Shatterfield
          uses it for its own bookkeeping and the writes it would make
          are already no-ops under the lock. */}
      <Shatterfield
        code={replay.meta?.code || "REPLAY"}
        playerIdx={seat}
        isNPC={replay.meta?.mode === "npc"}
        collection={{}}
        replayFeed={feed}
        onExit={onClose}
      />

      {/* THE TRANSPORT — over the top, its own layer, nothing below it
          can be clicked through to anyway because the lock is on. */}
      <div style={{
        position: "fixed", left: "50%", bottom: 14, transform: "translateX(-50%)",
        zIndex: 960, display: "flex", alignItems: "center", gap: 10,
        padding: "9px 14px", borderRadius: 999,
        background: "rgba(6,4,12,0.94)", border: `1px solid ${GM.pageBorder}`,
        boxShadow: "0 8px 28px rgba(0,0,0,0.8)", fontFamily: GM.font,
        maxWidth: "min(94vw, 620px)",
      }}>
        <button onClick={() => feed.toggle()} style={{ ...btn(GM.inkGoldBright), fontSize: 13, padding: "4px 10px" }}>
          {prog.playing ? "❚❚" : "▶"}
        </button>

        <input
          type="range" min={0} max={Math.max(1, prog.total)} value={prog.idx}
          onChange={(e) => feed.seek(Number(e.target.value))}
          aria-label="Replay position"
          style={{ flex: 1, minWidth: 120, accentColor: GM.inkGold }}
        />

        <span style={{ fontSize: 10, color: GM.vellumFaint, whiteSpace: "nowrap" }}>
          {fmtDur(prog.ms)} / {fmtDur(prog.totalMs)}
        </span>

        {SPEEDS.map((sp) => (
          <button key={sp} onClick={() => feed.setSpeed(sp)}
            style={{ ...btn(prog.speed === sp ? GM.inkGoldBright : GM.vellumFaint), padding: "4px 8px" }}>
            {sp}×
          </button>
        ))}

        <button aria-label="Close" onClick={onClose} style={{ ...btn(GM.vellumDim), padding: "4px 9px" }}>✕</button>
      </div>

      {/* The one thing a viewer must never be confused about. */}
      <div style={{
        position: "fixed", top: 10, left: "50%", transform: "translateX(-50%)",
        zIndex: 960, fontSize: 9, letterSpacing: 3, color: GM.inkGoldBright,
        background: "rgba(6,4,12,0.9)", border: `1px solid ${GM.pageBorder}`,
        borderRadius: 999, padding: "4px 12px", fontFamily: GM.font, pointerEvents: "none",
      }}>
        🎞 REPLAY — the board is a recording
      </div>
    </div>
  ), document.body);
}

const shellStyle = {
  position: "fixed", inset: 0, zIndex: 950, background: MANUSCRIPT_BG,
  display: "flex", flexDirection: "column", alignItems: "center",
  justifyContent: "center", fontFamily: GM.font,
};

export default ReplayShelf;
